/**
 * CLI Redis Passthrough
 *
 * Allows superadmins to execute read-only Redis commands against the production cache.
 */

import process from "node:process";
import { resolveConfig } from "./auth.js";
import { writeRawOutput } from "./brand.js";
import { fetchOrExit } from "./api-fetch.js";

const MAX_COMMAND_SIZE = 10_000; // 10KB - reasonable for interactive use

type RedisResponse = {
  ok: boolean;
  data?: unknown;
  durationMs?: number;
  error?: string;
  code?: "DISABLED" | "BLOCKED";
};

function hasFlag(argv: string[], ...flags: string[]): boolean {
  return flags.some((flag) => argv.includes(flag));
}

/**
 * Formats Redis response for display.
 */
function formatOutput(data: unknown): string {
  if (data === null) {
    return "(nil)";
  }

  if (typeof data === "string") {
    return data;
  }

  if (typeof data === "number") {
    return `(integer) ${data}`;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return "(empty array)";
    }
    return data
      .map((item, index) => `${index + 1}) ${formatOutput(item)}`)
      .join("\n");
  }

  if (typeof data === "object") {
    return JSON.stringify(data, null, 2);
  }

  return String(data);
}

export async function runRedis(argv: string[]): Promise<void> {
  const { apiUrl, token } = await resolveConfig(argv);

  const jsonOutput = hasFlag(argv, "--json");

  // Get command: remaining args after filtering flags
  const flagsWithValues = ["--api-url", "--token", "--env"];
  const commandParts: string[] = [];
  for (let i = 0; i < argv.length; i++) {
    if (flagsWithValues.includes(argv[i])) {
      i++; // Skip flag value
      continue;
    }
    if (argv[i].startsWith("--")) continue;
    commandParts.push(argv[i]);
  }
  const command = commandParts.join(" ");

  if (!command) {
    console.error("Error: No command provided.");
    console.error("");
    console.error("Usage: canary redis <command> [--json]");
    console.error('       canary redis KEYS "job:*"');
    console.error("");
    console.error("Examples:");
    console.error("  canary redis PING");
    console.error("  canary redis INFO");
    console.error('  canary redis KEYS "job:*"');
    console.error('  canary redis HGETALL "job:123:status"');
    console.error('  canary redis GET "some:key" --json');
    process.exit(1);
  }

  if (command.length > MAX_COMMAND_SIZE) {
    console.error(`Error: Command too large (${command.length} chars, max ${MAX_COMMAND_SIZE})`);
    process.exit(1);
  }

  try {
    const res = await fetchOrExit(`${apiUrl}/superadmin/redis`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      token,
      body: JSON.stringify({ command }),
    });

    // Handle HTTP errors before parsing JSON
    if (!res.ok) {
      const text = await res.text();

      if (res.status === 404) {
        console.error("Error: Endpoint not found. The redis feature may not be deployed to this environment.");
        process.exit(1);
      }

      // Try to parse error as JSON, fallback to raw text
      try {
        const errorJson = JSON.parse(text) as { error?: string };
        console.error(`Error: ${errorJson.error ?? text}`);
      } catch {
        console.error(`Error (${res.status}): ${text || res.statusText}`);
      }
      process.exit(1);
    }

    const json_response = (await res.json()) as RedisResponse;

    if (!json_response.ok) {
      console.error(`Error: ${json_response.error}`);
      if (json_response.code === "BLOCKED") {
        console.error("The Redis debugging feature has been disabled due to this blocked command.");
      }
      process.exit(1);
    }

    if (jsonOutput) {
      writeRawOutput(JSON.stringify(json_response.data, null, 2));
    } else {
      writeRawOutput(formatOutput(json_response.data));
      console.log(`\nTime: ${json_response.durationMs}ms`);
    }
  } catch (err) {
    console.error(`Failed to execute command: ${err}`);
    process.exit(1);
  }
}
